import React, { useState, useEffect } from 'react';
import axios from 'axios';
import configData from '../Config.json';
import { useStateValue } from '../StateProvider';
import Title from '../Title';
import { getPermission } from '../Utils/permissions';
import Select from '../Components/select.js';

const templateTypes = [
    {
        id: 'statementofaccountclient',
        name: 'Statement of Account - Client',
        variables: ['recipient', 'clientname', 'currencysymbol', 'currencyname', 'balance', 'date', 'projectname', 'username']
    },
    {
        id: 'transactionsreceipt',
        name: 'Transactions - Receipt',
        variables: ['recipient', 'clientname', 'id', 'description', 'currencysymbol', 'currencyname', 'amount', 'amountwords', 'date', 'projectname', 'username']
    },
    {
        id: 'transactionspayment',
        name: 'Transactions - Payment',
        variables: ['recipient', 'id', 'description', 'currencysymbol', 'currencyname', 'amount', 'amountwords', 'date', 'datetime', 'projectname', 'username']
    },
    {
        id: 'collectionsreceipt',
        name: 'Collections - Receipt',
        variables: ['recipient', 'id', 'description', 'currencysymbol', 'currencyname', 'amount', 'amountwords', 'date', 'datetime', 'projectname', 'username']
    },
    {
        id: 'collectionspayment',
        name: 'Collections - Payment',
        variables: ['recipient', 'id', 'description', 'currencysymbol', 'currencyname', 'amount', 'amountwords', 'date', 'datetime', 'projectname', 'username']
    }
]

function Templates({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [selectedtemplatetype, setSelectedtemplatetype] = useState('statementofaccount');

    const [templates, setTemplates] = useState([]);
    const [selectedtemplateid, setSelectedtemplateid] = useState('');
    const [templatecontent, setTemplatecontent] = useState('');

    const [availablevars, setAvailablevars] = useState([]);
    const [detectedvars, setDetectedvars] = useState([]);
    const [preview, setPreview] = useState('');

    const [result, setResult] = useState('');
    const [loading, setLoading] = useState(false);

    const sampledata = {
        clientname: '1st floor - Mr. XYZ',
        suppliername: 'Supplier XYZ',
        recipient: 'John Doe',
        projectname: project?.name || 'Sample Project',
        id: 'ID9038102',
        currencysymbol: '$',
        currencyname: 'USD',
        balance: '500',
        amount: '500',
        amountwords: 'Five hundred',
        description: 'Maintenance Fee',
        date: '22 Oct 2025',
        datetime: '22 Oct 2025 04:39:34 PM',
        username: state.user.username || 'Joe Doe'
    };

    useEffect(() => {
        const filtered = project.templates
            .filter(template => template.type === selectedtemplatetype)
            .map((template, index) => ({
                id: template.id,
                name: template.content.slice(0, 30),
                content: template.content
            }));

        setTemplates(filtered);
    }, [selectedtemplatetype]);

    useEffect(() => {
        if (templates.length > 0) {
            setSelectedtemplateid(templates[0].id);
        } else {
            setSelectedtemplateid('');
            setTemplatecontent('');
        }
    }, [templates]);

    useEffect(() => {
        const selected = templates.find(t => t.id === selectedtemplateid);
        setTemplatecontent(selected?.content || '');
    }, [selectedtemplateid]);

    useEffect(() => {
        const typeObj = templateTypes.find(t => t.id === selectedtemplatetype);
        if (typeObj) setAvailablevars(typeObj.variables);
    }, [selectedtemplatetype]);

    useEffect(() => {
        const matches = [...templatecontent.matchAll(/{(.*?)}/g)].map(m => m[1]);
        setDetectedvars(matches);
    }, [templatecontent]);

    useEffect(() => {
        let content = templatecontent;

        Object.entries(sampledata).forEach(([key, value]) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            content = content.replace(regex, value);
        });

        content = content.replace(/\n/g, '<br>');

        setPreview(content);
    }, [templatecontent, sampledata]);

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const exportPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ExportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    const importPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ImportPage />, modaltype: 'small', modaltitle: 'Import' }));
    }

    function ExportPage() {
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!project || !project.templates === 0) {
                setResult('No data to export');
                return;
            }

            const importedContent = JSON.stringify(project.templates, null, 2);

            navigator.clipboard.writeText(importedContent)
                .then(() => setResult('Data copied to clipboard'))
                .catch(err => setResult('Error copying data to clipboard: ', err));
        }, []);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {result}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ImportPage() {
        const [importText, setImportText] = useState('');
        const [newEntries, setNewEntries] = useState([]);

        const [loading, setLoading] = useState(false);
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!importText) {
                setResult('');
                setNewEntries([]);
                return;
            }

            let isJSON = false;
            let data = [];

            try {
                const jsonData = JSON.parse(importText);
                isJSON = true;
                data = jsonData;
            } catch {
                isJSON = false;
            }

            if (isJSON) {
                const requiredAttributes = ["id", "type", "content"];
                const isValidData = data.every(entry =>
                    requiredAttributes.every(attr => entry.hasOwnProperty(attr))
                );

                if (isValidData) {
                    const newEntries = data.filter(entry => {
                        return !project.templates.some(template =>
                            template.id === entry.id
                        );
                    });

                    if (newEntries.length > 0) {
                        setNewEntries(newEntries);
                        setResult('Data is valid. New entries found: ' + newEntries.length);
                    }
                    else {
                        setResult(data.length + " entries found. No new data.");
                    }
                }
                else {
                    setResult('Invalid data: Missing required attributes.');
                }
            }

            if (!isJSON) {
                setResult('Invalid format');
            }
        }, [importText]);

        const handleImport = () => {
            console.log('Trying to import data');

            if (newEntries.length === 0) {
                setResult('No new entries to import');
                return;
            }

            setLoading(true);
            setResult(null);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                templates: newEntries
            }

            axios.post(configData.CONTROLLERURL + configData.IMPORTTEMPLATES, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Import templates received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'IMPORT_TEMPLATES',
                            templates: newEntries
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });

            setResult('Data imported successfully.');
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <textarea
                                className='modal_textarea'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your import data here..."
                                rows={10}
                                cols={50}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleImport} disabled={loading}>{loading ? 'Loading...' : 'Import'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const handleAddTemplate = (type, content) => {
        console.log('Trying to add template');

        setResult('');

        const hasPermission = getPermission(
            project.projectuserid,
            project.users,
            state.user.userid,
            'Settings',
            'add',
            project.archived
        );

        if (hasPermission.code === 0) {
            setResult(hasPermission.data);
            return;
        }

        setLoading(true);

        const data = {
            projectuserid: project.projectuserid,
            projectid: project.projectid,
            type: type,
            content: content,
        }

        axios
            .post(
                configData.CONTROLLERURL + configData.ADDTEMPLATE, data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        userid: state.user.userid,
                        usertoken: state.user.usertoken,
                    },
                }
            )
            .then((res) => {
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        setResult('Template data successfully received');
                    }
                    else {
                        setResult('');
                    }
                }
            })
            .catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            })
            .finally(() =>
                setLoading(false)
            );
    };

    const handleUpdateTemplate = (id, type, content) => {
        console.log('Trying to update template');

        setResult('');

        const hasPermission = getPermission(
            project.projectuserid,
            project.users,
            state.user.userid,
            'Settings',
            'add',
            project.archived
        );

        if (hasPermission.code === 0) {
            setResult(hasPermission.data);
            return;
        }

        setLoading(true);

        const data = {
            projectuserid: project.projectuserid,
            projectid: project.projectid,
            id: id,
            type: type,
            content: content,
        }

        axios.post(
            configData.CONTROLLERURL + configData.UPDATETEMPLATE, data,
            {
                headers: {
                    'Content-Type': 'application/json',
                    userid: state.user.userid,
                    usertoken: state.user.usertoken,
                },
            }
        ).then((res) => {
            console.log('Update template data received')
            console.log(res.data)
            if (res.data instanceof Object) {
                if (res.data.code === 1) {
                    setResult('Template data successfully received');
                }
                else {
                    setResult('');
                }
            }
        }).catch((err) => {
            setResult(err.response?.data?.message || err.message || 'Network error');
        }).finally(() =>
            setLoading(false)
        );
    };

    const handleRemoveTemplate = (id) => {
        console.log('Trying to remove template');

        setResult('');

        const hasPermission = getPermission(
            project.projectuserid,
            project.users,
            state.user.userid,
            'Settings',
            'add',
            project.archived
        );

        if (hasPermission.code === 0) {
            setResult(hasPermission.data);
            return;
        }

        setLoading(true);

        const data = {
            projectuserid: project.projectuserid,
            projectid: project.projectid,
            id: id
        }

        axios.post(
            configData.CONTROLLERURL + configData.REMOVETEMPLATE, data,
            {
                headers: {
                    'Content-Type': 'application/json',
                    userid: state.user.userid,
                    usertoken: state.user.usertoken,
                },
            }
        )
            .then((res) => {
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        setResult('Template data successfully received');
                    }
                    else {
                        setResult('');
                    }
                }
            })
            .catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            })
            .finally(() =>
                setLoading(false)
            );
    };

    return (
        <div className="templates">
            <Title text="Custom Templates" />
            <div className="buttonscontainer">
                {result && <div className='modal_result'>{result}</div>}
                {
                    selectedtemplateid ?
                        <button className="button" onClick={() => handleUpdateTemplate(selectedtemplateid, selectedtemplatetype, templatecontent)} disabled={loading}>
                            {loading ? 'Saving...' : 'Update Template'}
                        </button>
                        :
                        <button className="button" onClick={() => handleAddTemplate(selectedtemplatetype, templatecontent)} disabled={loading}>
                            {loading ? 'Saving...' : 'Save Template'}
                        </button>
                }
                <button className="button" onClick={() => handleRemoveTemplate(selectedtemplateid)} disabled={loading}>
                    {loading ? 'Removing...' : 'Remove Template'}
                </button>
            </div>

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={exportPage}>Export</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={importPage}>Import</button>
                </div>
            </div>

            <div className="modal_row">
                <div className="modal_rowsection">
                    <label className="modal_label">Template Type</label>
                </div>
                <div className="modal_rowsection">
                    <Select data={templateTypes} itemid={selectedtemplatetype} setItemid={setSelectedtemplatetype} />
                </div>
            </div>

            <div className="modal_row">
                <div className="modal_rowsection">
                    <label className="modal_label">Templates</label>
                </div>
                <div className="modal_rowsection">
                    <Select data={templates} itemid={selectedtemplateid} setItemid={setSelectedtemplateid} />
                </div>
            </div>

            <div className="modal_row">
                <div className="modal_rowsection">
                    <label className="modal_label">Template Content</label>
                </div>
                <div className="modal_rowsection">
                    <textarea
                        className="modal_textarea"
                        rows={10}
                        placeholder="Write your template here using {variables}..."
                        value={templatecontent}
                        onChange={(e) => setTemplatecontent(e.target.value)}
                    />
                </div>
            </div>

            <div className="modal_row">
                <div className="modal_rowsection">
                    <div className="modal_label">Available Variables</div>
                </div>
                <div className="modal_rowsection">
                    {
                        availablevars.map((v, i) => {
                            const isUsed = detectedvars.includes(v);
                            return (
                                <div
                                    key={'vars' + i}
                                    style={{
                                        backgroundColor: '#1e1e1e33',
                                        color: '#d86800ff',
                                        padding: '3px 8px',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        margin: '0 4px',
                                        display: 'inline-block',
                                    }}
                                >
                                    {v}
                                </div>
                            );
                        })
                    }
                </div>
            </div>

            <div className="modal_row">
                <div className="modal_rowsection">
                    <div className="modal_label">Used Variables</div>
                </div>
                <div className="modal_rowsection">
                    {
                        detectedvars.map((v, i) => (
                            <div
                                key={'var' + i}
                                style={{
                                    backgroundColor: '#FFA50033',
                                    color: '#D87A00',
                                    padding: '3px 8px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    margin: '0 4px',
                                    display: 'inline-block',
                                }}
                            >
                                {v}
                            </div>
                        ))
                    }
                </div>
            </div>

            <div className="modal_row">
                <div className="modal_rowsection">
                    <div className="modal_label">Live Preview</div>
                </div>
                <div className="modal_rowsection">
                    <div
                        style={{
                            border: '1px solid #ccc',
                            padding: '10px',
                            borderRadius: '5px',
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#f9f9f9'
                        }}
                        dangerouslySetInnerHTML={{ __html: preview }}
                    />
                </div>
            </div>
        </div>
    );
}

export default Templates;